import React, { useContext } from 'react';
import styled from 'styled-components';
import { isBefore } from 'date-fns';
import { Alert } from '@brokerbay/design-components';
import { AnsweringSubscriptionLevel, Timezone } from '@bb/domain/dist/types';
import { AnsweringServiceType } from '@bb/domain/dist/types/AnsweringSubscription';
import { Icon } from 'components';
import { createFormatDateFunction, hasValidStripeMlsProductId } from 'utils';
import { AuthContext } from 'contexts';
import { shiftToUtc } from '../../onboarding/util';
import AppointmentDeskContext from './AppointmentDeskContext';

type AlertType = 'success' | 'info' | 'warning' | 'error';

const generateAlertProps = ({
  alertType,
  isSubscribed,
  answeringLaunchDate,
  timezone,
  currentStep,
  brokerageLaunchDate,
  now,
  serviceType,
}: AppointmentDeskAlertProps & {
  serviceType?: AnsweringServiceType;
}) => {
  let icon = <Icon type="antdCloseCircleFilled" color="danger" width={14} />;
  let type: AlertType = 'error';
  let message = `You currently do not have live concierge service set up for your ${
    alertType === 'org' ? 'brokerage' : 'listings'
  }.`;
  let $visible = true;

  // AnsweringService launchDate should be at the start of date from BE
  const datePassed =
    !answeringLaunchDate || isBefore(new Date(answeringLaunchDate), now);

  if (currentStep === 1) {
    // TODO: may need updating when arcobaleno implements launchDate in brokerage onboarding.
    // Currently takes UTC start of day saved in date object and formats it as date string.
    const formattedLaunchDate = createFormatDateFunction('MMMM do yyyy')(
      brokerageLaunchDate,
      Timezone['America/Toronto'],
    );
    message = `Your Brokerage Launch date is ${formattedLaunchDate}.`;
    icon = <Icon type="antdInfoCircleFilled" color="info" width={14} />;
    type = 'info';
  } else if (isSubscribed && datePassed) {
    icon = <Icon type="antdCheckCircleFilled" color="success" width={14} />;
    type = 'success';
    message = 'Your concierge service is live.';
    $visible = serviceType !== AnsweringServiceType.AppointmentDesk;
  } else if (isSubscribed && !datePassed) {
    icon = <Icon type="antdClockCircle" color="info" width={14} />;
    type = 'info';
    const dateFormatted = createFormatDateFunction('MMMM do yyyy')(
      answeringLaunchDate,
      timezone,
    );
    message = `Your live concierge service starts on ${dateFormatted}.`;
  }

  return {
    icon,
    type,
    message,
    $visible,
  };
};

interface AppointmentDeskAlertProps {
  alertType: AnsweringSubscriptionLevel;
  isSubscribed: boolean;
  answeringLaunchDate?: Date;
  timezone?: Timezone;
  currentStep: number;
  brokerageLaunchDate?: string;
  now?: Date;
}

const AppointmentDeskAlert: React.FC<AppointmentDeskAlertProps> = ({
  alertType,
  isSubscribed,
  answeringLaunchDate,
  timezone,
  currentStep,
  brokerageLaunchDate,
}) => {
  const now = new Date();
  const { answeringSubscription } = useContext(AppointmentDeskContext);
  const { answeringPortalConfig } = useContext(AuthContext);

  if (
    currentStep === 1 &&
    (!brokerageLaunchDate || isBefore(shiftToUtc(brokerageLaunchDate), now))
  ) {
    return null;
  }

  return (
    <>
      {hasValidStripeMlsProductId(answeringPortalConfig?.mlsProductId) &&
        answeringSubscription?.serviceType ===
          AnsweringServiceType.AppointmentDesk && (
          <StyledAlert
            data-cy="appointmentDesk-upgrade-alert"
            showIcon
            message="You currently have appointment desk set up, upgrade to live concierge service here"
            icon={
              <Icon type="antdInfoCircleFilled" color="warning" width={14} />
            }
            type="warning"
          />
        )}
      <StyledAlert
        data-cy="appointmentDesk-alert"
        showIcon
        {...generateAlertProps({
          alertType,
          isSubscribed,
          answeringLaunchDate,
          timezone,
          currentStep,
          brokerageLaunchDate,
          now,
          ...(hasValidStripeMlsProductId(
            answeringPortalConfig?.mlsProductId,
          ) && {
            serviceType: answeringSubscription?.serviceType,
          }),
        })}
      />
    </>
  );
};

export default AppointmentDeskAlert;

const StyledAlert = styled(Alert)<{
  $visible?: boolean;
}>`
  margin-bottom: 16px;
  ${({ $visible }) => $visible === false && 'display: none;'}
`;
